import {
  type PasskeyWithMetadata,
  type PrivyEvents,
  useLoginWithPasskey,
  type User,
  useSignupWithPasskey,
} from '@privy-io/react-auth';
import { useCallback, useRef } from 'react';

import { type PasskeySignIn, PasskeySignInMode } from './wallet-access-flow';

type LoginResult = Parameters<NonNullable<PrivyEvents['login']['onComplete']>>[0];

export function usePasskeySignIn(): (
  mode: PasskeySignInMode,
  credentialId: string | null,
) => Promise<PasskeySignIn | null> {
  const completed = useRef<PasskeySignIn | null>(null);
  const onComplete = useCallback((result: LoginResult) => {
    // Only a fresh passkey sign-in counts; an existing or non-passkey session would unlock without the passkey.
    if (!result.wasAlreadyAuthenticated && result.loginMethod === 'passkey') {
      completed.current = {
        privyUserId: result.user.id,
        credentialId: lastVerifiedPasskey(result.user),
      };
    }
  }, []);
  const { signupWithPasskey } = useSignupWithPasskey({ onComplete });
  const { loginWithPasskey } = useLoginWithPasskey({ onComplete });

  return useCallback(
    async (mode: PasskeySignInMode, credentialId: string | null) => {
      completed.current = null;

      if (mode === PasskeySignInMode.Login) {
        // Offering only the remembered passkey skips the picker; choosing another passkey forgets it first.
        await loginWithPasskey(credentialId ? { credentialIds: [credentialId] } : undefined);
      } else {
        await signupWithPasskey();
      }

      return completed.current;
    },
    [loginWithPasskey, signupWithPasskey],
  );
}

// Privy does not report which passkey was just used, so the most recently verified one stands in for it.
function lastVerifiedPasskey(user: User): string | null {
  const passkeys = user.linkedAccounts.filter(
    (account): account is PasskeyWithMetadata => account.type === 'passkey',
  );
  const latest = passkeys.reduce<PasskeyWithMetadata | null>(
    (newest, passkey) => (!newest || verifiedAt(passkey) > verifiedAt(newest) ? passkey : newest),
    null,
  );
  return latest?.credentialId ?? null;
}

function verifiedAt(passkey: PasskeyWithMetadata): number {
  return passkey.latestVerifiedAt?.getTime() ?? 0;
}
